import { ChangeDetectorRef, Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, map, Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DialogService, TABLE_SELECTOR_HANDLER } from '@cbre/components';
import { PropertySetDetailsMode, PropertySetsState } from '../../property-sets-dialog/property-sets-dialog';
import { STATE_PROVIDER, StateProvider } from '../../services/state-provider/state-provider';
import { COLUMNS_TO_SHOW, ColumnToShow } from './property-set-records-table.config';
import { PropertySetRecordsSelectionHandleService } from './property-set-records-selection-handle.service';
import { Sort } from '@angular/material/sort';
import { PermissionLevel, PropertySet } from '@miq/domain/property-sets';
import { MatMenuTrigger } from '@angular/material/menu';
import { CbreAlertService } from '@cbre/alerts';
import { PropertySetsHttpService } from '../../services/http/property-sets-http.service';
import { PageEvent } from '@angular/material/paginator';
import { SelectionModel } from '@angular/cdk/collections';
import { PropertySetInfoDialogComponent } from '../property-set-info-dialog/property-set-info-dialog.component';
import { L10nTranslationService } from 'angular-l10n';

@UntilDestroy()
@Component({
  selector: 'miq-property-set-records',
  templateUrl: './property-set-records.component.html',
  styleUrls: ['./property-set-records.component.scss'],
  providers: [{ provide: TABLE_SELECTOR_HANDLER, useClass: PropertySetRecordsSelectionHandleService }],
  standalone: false,
})
export class PropertySetRecordsComponent implements OnInit {
  @Input() set propertySet(propertySet: PropertySet) {
    // all this logic should be triggered every time the property set from the state is changed.
    this._propertySet = propertySet;
    this.#setColumns(propertySet);
    this.canUserAccess = propertySet?.level !== PermissionLevel.Read;
    this.selectionHandlerSrv.propertySetId = propertySet?.id;
    this.selectionHandlerSrv.propertySetFingerPrint = propertySet?.fingerprint;
    this.selectionHandlerSrv.resetValues();
    this.selectionHandlerSrv.loadProperties();
    this.selectionHandlerSrv.configure(this.selectionHandlerSrv.dataSource, new SelectionModel(true, []));
    this.changeDetectorRef.detectChanges();
  }

  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
  public editModeTooltip = 'Save Property Set Details before adding or removing Records';
  public readonly MODE = PropertySetDetailsMode;
  public mode$: Observable<PropertySetDetailsMode>;
  public columns: ColumnToShow[] = [];
  public searchControl = new FormControl<string>(null);
  public totalCount: number = null;
  public canUserAccess = false;
  private _propertySet: PropertySet;

  constructor(
    @Inject(TABLE_SELECTOR_HANDLER) public selectionHandlerSrv: PropertySetRecordsSelectionHandleService<any>,
    @Inject(STATE_PROVIDER) private propertySetDetailsState: StateProvider<PropertySetsState>,
    private propertySetsHttpService: PropertySetsHttpService,
    private cbreAlertService: CbreAlertService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private translationService: L10nTranslationService
  ) {}

  ngOnInit(): void {
    this.mode$ = this.propertySetDetailsState.stateChanges$.pipe(
      untilDestroyed(this),
      map(state => state.mode),
      distinctUntilChanged()
    );
    this.selectionHandlerSrv.deletedRecords = [];
    const { entity } = this.propertySetDetailsState.snapshot();
    this.selectionHandlerSrv.entity = entity;
    this.searchControl.valueChanges.pipe(untilDestroyed(this), debounceTime(300)).subscribe(searchText => {
      this.selectionHandlerSrv.searchTextInput = searchText;
      this.selectionHandlerSrv.loadProperties();
    });
  }

  #setColumns(propertySet: PropertySet) {
    this.columns = COLUMNS_TO_SHOW.map(c => ({ ...c, context: { propertySet } }));
  }

  clear() {
    this.searchControl.setValue(null);
  }

  search(searchText: string) {
    this.selectionHandlerSrv.searchTextInput = searchText;
    this.selectionHandlerSrv.loadProperties();
    this.changeDetectorRef.detectChanges();
  }

  onSortChange(sortData: Sort) {
    this.selectionHandlerSrv.onSortChange(sortData);
    this.changeDetectorRef.detectChanges();
  }

  onPageChange(event: PageEvent) {
    this.selectionHandlerSrv.onPageChange(event);
    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.detectChanges();
  }

  onPropertySelected(usageId) {
    this.propertySetsHttpService.addPropertiesToPropertySet(this._propertySet.id, [usageId]).subscribe(() => {
      this.cbreAlertService.showSuccessSnackBar(`Record added to the Property Set`);
      this.selectionHandlerSrv.loadProperties();
      this.changeDetectorRef.detectChanges();
    });
  }

  //get usage id from the selected property and delete it from the property set
  getUsageIds() {
    const usageIds: number[] = this.selectionHandlerSrv.selectedItems
      .flatMap(item => item.usages)
      .filter(usage =>
        usage.record_set?.some(record => record.set_id === this.selectionHandlerSrv.propertySetFingerPrint)
      )
      .map(usage => usage.id);

    return usageIds;
  }

  deleteSelectedProperties() {
    if (this.selectionHandlerSrv.selectedItems) {
      const numberDeletedRecord = this.selectionHandlerSrv.selectedItems.length;
      const deleteRecordIds = this.getUsageIds();
      this.propertySetsHttpService
        .deletePropertiesToPropertySet(this._propertySet.id, deleteRecordIds)
        ?.subscribe(() => {
          this.selectionHandlerSrv.clearSelection();
          this.selectionHandlerSrv.deletedRecords.push(...deleteRecordIds);
          this.selectionHandlerSrv.loadProperties();
          this.changeDetectorRef.detectChanges();
          this.openNoticeDialog(numberDeletedRecord);
        });
    }
  }

  openNoticeDialog(numberDeletedRecord) {
    const data = {
      context: 'remove',
      removingMessage: this.translationService.translate('propertySet.removingPropertySetNotice', {
        recordCount: numberDeletedRecord,
        propetSetName: this._propertySet.name,
      }),
    };
    const options = { panelClass: 'alert-modals' };
    this.dialogService.openDialog(PropertySetInfoDialogComponent, data, options).pipe(map(() => true));
  }
}
